import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc, writeBatch, serverTimestamp, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import { VALIDATION_RULES } from '../utils/constants';
import { validateMaxLength } from '../utils/helpers';
import { compressImageFile } from '../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { showToast } from '../utils/toast';
import './EditProfile.css';

// 許可するドメインリスト
const ALLOWED_DOMAINS = [
  'youtube.com',
  'youtu.be',
  'twitter.com',
  'x.com',
  'instagram.com',
  'facebook.com',
  'tiktok.com',
  'yamareco.com',
  'yamap.com',
  'google.com',
  'github.com'
];

// デフォルト値（後方互換: 既存Firestoreデータとのマージ用）
const DEFAULT_EXPERIENCE = {
  fewMountains: true,
  noClimbingBoots: true,
  noTentExperience: true,
  noHutExperience: true,
  noSnowExperience: true
};

// ヤマッチ連携プロフィール項目
const GENDER_OPTIONS = ['未回答', '男性', '女性', 'その他'];
const AGE_GROUP_OPTIONS = ['未回答', '10代', '20代', '30代', '40代', '50代', '60代以上'];
const PREFECTURES = [
  '北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県',
  '茨城県','栃木県','群馬県','埼玉県','千葉県','東京都','神奈川県',
  '山梨県','新潟県','富山県','石川県','福井県','長野県','岐阜県',
  '静岡県','愛知県','三重県','滋賀県','京都府','大阪府','兵庫県',
  '奈良県','和歌山県','鳥取県','島根県','岡山県','広島県','山口県',
  '徳島県','香川県','愛媛県','高知県','福岡県','佐賀県','長崎県',
  '熊本県','大分県','宮崎県','鹿児島県','沖縄県'
];

const MONTHLY_FREQUENCY_OPTIONS = ['未回答', '月1回未満', '月1〜2回', '月3〜4回', '週1回以上'];
const HIKING_STYLE_OPTIONS = [
  { key: 'dayHike', label: '日帰り登山' },
  { key: 'hutStay', label: '山小屋泊' },
  { key: 'tentStay', label: 'テント泊' },
  { key: 'trail', label: 'トレイルラン' },
  { key: 'photo', label: '写真撮影目的' }
];
const PACE_OPTIONS = ['未回答', 'ゆっくりめ（標準CTの1.2倍以上）', '標準（標準CT通り）', '速め（標準CTの0.8倍以下）'];
const FITNESS_LEVEL_OPTIONS = [
  '未回答',
  'レベル1（〜2時間）',
  'レベル2（2〜4時間）',
  'レベル3（4〜6時間）',
  'レベル4（6〜8時間）',
  'レベル5（8〜10時間）',
  'レベル6（10〜12時間）',
  'レベル7（12時間〜）'
];
// 体力レベルは「8kgの登山ザックを背負ってどのくらい歩くのが余裕か」を基準にした7段階
const ALTITUDE_EXPERIENCE_OPTIONS = [
  { key: 'lowMountain', label: '低山（〜1000m）' },
  { key: 'midMountain', label: '中山（1000〜2000m）' },
  { key: 'highMountain', label: '高山（2000〜3000m）' },
  { key: 'alpineMountain', label: '3000m超（アルプス等）' }
];
const TECHNICAL_EXPERIENCE_OPTIONS = [
  { key: 'chainField', label: '鎖場の通過経験あり' },
  { key: 'rockField', label: '岩場・バリエーション経験あり' },
  { key: 'snowMountain', label: '雪山（冬山）登山経験あり' },
  { key: 'crampon', label: 'アイゼン使用経験あり' },
  { key: 'iceAxe', label: 'ピッケル使用経験あり' }
];
const GEAR_OPTIONS = [
  { key: 'helmet', label: 'ヘルメット' },
  { key: 'crampon', label: 'アイゼン' },
  { key: 'iceAxe', label: 'ピッケル' },
  { key: 'tent', label: 'テント一式' },
  { key: 'winterGear', label: '冬山装備一式' }
];

// フィールド名の日本語マッピング
const FIELD_LABELS = {
  nickname: 'ニックネーム',
  bio: '自己紹介',
  climbingHistory: '登山歴',
  favoriteMountains: '好きな山',
  yamapLink: 'YAMAP',
  yamarecoLink: 'ヤマレコ',
  twitterLink: 'Twitter',
  youtubeLink: 'YouTube',
  instagramLink: 'Instagram'
};

// フィールドごとの最大文字数
const FIELD_MAX_LENGTHS = {
  nickname: VALIDATION_RULES.NICKNAME_MAX_LENGTH,
  bio: VALIDATION_RULES.BIO_MAX_LENGTH,
  climbingHistory: VALIDATION_RULES.DESCRIPTION_MAX_LENGTH,
  favoriteMountains: VALIDATION_RULES.DESCRIPTION_MAX_LENGTH
};

const buildProfileUpdatePayload = (formData, photoURL, climbingExperience) => {
  const rawLink = formData.twitterLink || '';
  const twitterHandle = rawLink
    ? (rawLink.split('x.com/')[1] || rawLink.split('twitter.com/')[1] || '').split('/')[0].split('?')[0]
    : '';

  return {
    nickname: formData.nickname.trim(),
    bio: formData.bio || '',
    climbingHistory: formData.climbingHistory || '',
    favoriteMountains: formData.favoriteMountains || '',
    nearestStation: formData.nearestStation || '',
    hasCar: !!formData.hasCar,
    gender: formData.gender || '',
    ageGroup: formData.ageGroup || '',
    residencePrefecture: formData.residencePrefecture || '',
    monthlyFrequency: formData.monthlyFrequency || '',
    hikingStyle: Array.isArray(formData.hikingStyle) ? formData.hikingStyle : [],
    pace: formData.pace || '',
    fitnessLevel: formData.fitnessLevel || '',
    altitudeExperience: Array.isArray(formData.altitudeExperience) ? formData.altitudeExperience : [],
    technicalExperience: Array.isArray(formData.technicalExperience) ? formData.technicalExperience : [],
    ownedGear: Array.isArray(formData.ownedGear) ? formData.ownedGear : [],
    yamapLink: formData.yamapLink || '',
    yamarecoLink: formData.yamarecoLink || '',
    twitterLink: rawLink,
    twitterHandle,
    youtubeLink: formData.youtubeLink || '',
    instagramLink: formData.instagramLink || '',
    photoURL: photoURL || '',
    climbingExperience: climbingExperience || {},
    mountainInsurance: formData.mountainInsurance || '',
    cocoheriNumber: formData.cocoheriNumber || '',
  };
};

// URLバリデーション関数（改善版）
const validateUrl = (url, fieldName) => {
  if (!url) return { isValid: true, message: '' };

  // X(Twitter)リンクは https://x.com/ で始まることを要求
  if (fieldName === 'twitterLink') {
    if (!url.startsWith('https://x.com/')) {
      return {
        isValid: false,
        message: 'X(Twitter)のURLは https://x.com/ で始まる形式で入力してください。（例: https://x.com/username）'
      };
    }
    return { isValid: true, message: '' };
  }

  try {
    // プロトコルがなければ自動補完
    let processedUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      processedUrl = 'https://' + url;
    }

    const urlObj = new URL(processedUrl);
    const domain = urlObj.hostname.toLowerCase();

    // 許可ドメインチェック
    const isAllowed = ALLOWED_DOMAINS.some(allowedDomain =>
      domain === allowedDomain || domain.endsWith('.' + allowedDomain)
    );

    if (!isAllowed) {
      return {
        isValid: false,
        message: `${FIELD_LABELS[fieldName]}に無効なURLが指定されています。許可されているサイト: ${ALLOWED_DOMAINS.join(', ')}`
      };
    }

    // プロトコルチェック
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return {
        isValid: false,
        message: `${FIELD_LABELS[fieldName]}にはhttp://またはhttps://から始まるURLを指定してください。`
      };
    }

    return { isValid: true, message: '' };
  } catch (error) {
    return {
      isValid: false,
      message: `${FIELD_LABELS[fieldName]}に無効なURLが指定されています。正しいURL形式で入力してください。`
    };
  }
};

function EditProfile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [deletePhoto, setDeletePhoto] = useState(false);
  const profileImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nickname: '',
    bio: '',
    climbingHistory: '',
    favoriteMountains: '',
    yamapLink: '',
    yamarecoLink: '',
    twitterLink: '',
    youtubeLink: '',
    instagramLink: '',
    nearestStation: '',
    gender: '未回答',
    ageGroup: '未回答',
    residencePrefecture: '',
    hasCar: false,
    monthlyFrequency: '未回答',
    hikingStyle: [],
    pace: '未回答',
    fitnessLevel: '未回答',
    altitudeExperience: [],
    technicalExperience: [],
    ownedGear: [],
    mountainInsurance: '',
    cocoheriNumber: '',
    photoURL: ''
  });

  const [climbingExperience, setClimbingExperience] = useState({ ...DEFAULT_EXPERIENCE });
  const [oldNickname, setOldNickname] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (profileDoc.exists()) {
        const data = profileDoc.data();
        setFormData({
          nickname: data.nickname || '',
          bio: data.bio || '',
          climbingHistory: data.climbingHistory || '',
          favoriteMountains: data.favoriteMountains || '',
          yamapLink: data.yamapLink || '',
          yamarecoLink: data.yamarecoLink || '',
          twitterLink: data.twitterLink || '',
          youtubeLink: data.youtubeLink || '',
          instagramLink: data.instagramLink || '',
          nearestStation: data.nearestStation || '',
          gender: data.gender || '未回答',
          ageGroup: data.ageGroup || '未回答',
          residencePrefecture: data.residencePrefecture || '',
          hasCar: data.hasCar || false,
          monthlyFrequency: data.monthlyFrequency || '未回答',
          hikingStyle: data.hikingStyle || [],
          pace: data.pace || '未回答',
          fitnessLevel: data.fitnessLevel || '未回答',
          altitudeExperience: data.altitudeExperience || [],
          technicalExperience: data.technicalExperience || [],
          ownedGear: data.ownedGear || [],
          mountainInsurance: data.mountainInsurance || '',
          cocoheriNumber: data.cocoheriNumber || '',
          photoURL: data.photoURL || ''
        });
        if (data.photoURL) {
          setProfileImagePreview(data.photoURL);
        }
        if (data.climbingExperience) {
          setClimbingExperience({ ...DEFAULT_EXPERIENCE, ...data.climbingExperience });
        }
        setOldNickname(data.nickname || '');
      }
    } catch (error) {
      console.error('プロフィールの取得に失敗しました:', error);
      setError('プロフィールの取得に失敗しました。');
    } finally {
      setFetchLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser, fetchProfile]);

  const handleHikingStyleChange = (key) => {
    setFormData(prev => {
      const current = prev.hikingStyle || [];
      return {
        ...prev,
        hikingStyle: current.includes(key) ? current.filter(k => k !== key) : [...current, key]
      };
    });
  };

  const handleArrayFieldChange = (fieldName, key) => {
    setFormData(prev => {
      const current = prev[fieldName] || [];
      return {
        ...prev,
        [fieldName]: current.includes(key) ? current.filter(k => k !== key) : [...current, key]
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // エラーをクリア
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    // 基本バリデーション
    if (!formData.nickname.trim()) {
      setFieldErrors({ nickname: 'ニックネームを入力してください。' });
      setLoading(false);
      return;
    }

    // 文字数バリデーション
    const nicknameValidation = validateMaxLength(
      formData.nickname, 
      FIELD_MAX_LENGTHS.nickname, 
      FIELD_LABELS.nickname
    );
    if (!nicknameValidation.isValid) {
      setFieldErrors({ nickname: nicknameValidation.message });
      setLoading(false);
      return;
    }

    const bioValidation = validateMaxLength(
      formData.bio, 
      FIELD_MAX_LENGTHS.bio, 
      FIELD_LABELS.bio
    );
    if (!bioValidation.isValid) {
      setFieldErrors({ bio: bioValidation.message });
      setLoading(false);
      return;
    }

    const climbingHistoryValidation = validateMaxLength(
      formData.climbingHistory, 
      FIELD_MAX_LENGTHS.climbingHistory, 
      FIELD_LABELS.climbingHistory
    );
    if (!climbingHistoryValidation.isValid) {
      setFieldErrors({ climbingHistory: climbingHistoryValidation.message });
      setLoading(false);
      return;
    }

    const favoriteMountainsValidation = validateMaxLength(
      formData.favoriteMountains, 
      FIELD_MAX_LENGTHS.favoriteMountains, 
      FIELD_LABELS.favoriteMountains
    );
    if (!favoriteMountainsValidation.isValid) {
      setFieldErrors({ favoriteMountains: favoriteMountainsValidation.message });
      setLoading(false);
      return;
    }

    // URLバリデーション（フィールドごとのエラーを設定）
    const urls = {
      yamapLink: formData.yamapLink,
      yamarecoLink: formData.yamarecoLink,
      twitterLink: formData.twitterLink,
      youtubeLink: formData.youtubeLink,
      instagramLink: formData.instagramLink
    };

    const urlErrors = {};
    for (const [fieldName, url] of Object.entries(urls)) {
      if (url) {
        const validation = validateUrl(url, fieldName);
        if (!validation.isValid) {
          urlErrors[fieldName] = validation.message;
        }
      }
    }

    if (Object.keys(urlErrors).length > 0) {
      setFieldErrors(urlErrors);
      setLoading(false);
      return;
    }

    try {
      // モデレーションはバックグラウンドで実行（保存処理をブロックしない）
      guardContentSubmission({
        action: 'profile_update',
        contentType: 'profile',
        texts: [
          formData.nickname,
          formData.bio,
          formData.climbingHistory,
          formData.favoriteMountains,
        ],
        metadata: {
          userId: currentUser.uid,
        },
      }).then(showModerationWarning).catch(err => {
        console.warn('プロフィール更新の事前チェックに失敗しました:', err);
      });

      // プロフィール画像は Storage にアップロードしてから URL を Function に渡す
      let photoURL = deletePhoto ? '' : formData.photoURL;
      const profileStorageRef = ref(storage, `profile_images/${currentUser.uid}`);
      if (deletePhoto && formData.photoURL) {
        try {
          await deleteObject(profileStorageRef);
        } catch (deleteError) {
          console.warn('プロフィール画像の削除に失敗しました。プロフィール更新は継続します:', deleteError);
        }
      }
      if (!deletePhoto && profileImageFile) {
        try {
          const uploadResult = await uploadBytes(profileStorageRef, profileImageFile, {
            contentType: profileImageFile.type || 'image/jpeg',
          });
          photoURL = await getDownloadURL(uploadResult.ref);
        } catch (uploadError) {
          console.error('画像アップロードエラー:', uploadError);
          setError('画像のアップロードに失敗しました。ファイル形式・サイズを確認するか、画像なしで保存してください。（' + (uploadError.code || uploadError.message) + '）');
          setLoading(false);
          return;
        }
      }

      const profilePayload = buildProfileUpdatePayload(formData, photoURL, climbingExperience);

      // Firestore に直接保存（users + userStats を並列更新）
      await Promise.all([
        setDoc(doc(db, 'users', currentUser.uid), {
          ...profilePayload,
          updatedAt: serverTimestamp(),
        }, { merge: true }),
        setDoc(doc(db, 'userStats', currentUser.uid), {
          nickname: profilePayload.nickname,
          displayName: profilePayload.nickname,
          twitterHandle: profilePayload.twitterHandle,
          yamapLink: profilePayload.yamapLink,
          yamarecoLink: profilePayload.yamarecoLink,
          updatedAt: serverTimestamp(),
        }, { merge: true }),
      ]);
      updateAuthProfile(currentUser, {
        displayName: profilePayload.nickname,
      }).catch(authError => {
        console.warn('Auth displayName の同期に失敗しました:', authError);
      });

      // 非正規化：ニックネームが変わった場合のみ関連ドキュメントを更新（バックグラウンド）
      if (oldNickname !== '' && oldNickname !== formData.nickname) {
        updateUserNickname(currentUser.uid, formData.nickname).catch(console.error);
      }

      showToast({ message: 'プロフィールを更新しました！', type: 'success' });
      navigate('/profile');

    } catch (error) {
      console.error('プロフィールの更新に失敗しました:', error);
      setError('プロフィールの更新に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // 非正規化：ニックネーム変更時に関連データを更新
  // limit(100) で読み取り上限を設けている。100件を超えるユーザーは次回表示時に自動補完される
  const updateUserNickname = async (userId, newNickname) => {
    try {
      const batch = writeBatch(db);

      // 1. 募集ドキュメントのorganizer情報を更新（上限100件）
      const recruitmentsQuery = query(
        collection(db, 'recruitments'),
        where('userId', '==', userId),
        limit(100)
      );
      const recruitmentsSnapshot = await getDocs(recruitmentsQuery);

      recruitmentsSnapshot.docs.forEach(docSnapshot => {
        const recruitmentRef = doc(db, 'recruitments', docSnapshot.id);
        batch.update(recruitmentRef, {
          'organizer.nickname': newNickname,
          updatedAt: serverTimestamp()
        });
      });

      // 2. 応募ドキュメントのapplicant情報を更新（上限100件）
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', userId),
        limit(100)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);

      applicationsSnapshot.docs.forEach(docSnapshot => {
        const applicationRef = doc(db, 'applications', docSnapshot.id);
        batch.update(applicationRef, {
          'applicant.nickname': newNickname,
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      console.log('ニックネームの非正規化が完了しました');
    } catch (error) {
      console.error('ニックネームの非正規化に失敗しました:', error);
      // エラーをスローしない（プロフィール更新自体は成功）
    }
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('画像は5MB以下にしてください。');
      return;
    }
    try {
      setError('');
      setDeletePhoto(false);
      const compressedFile = await compressImageFile(file, {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.82,
        outputType: 'image/jpeg'
      });
      setProfileImageFile(compressedFile);
      setProfileImagePreview(URL.createObjectURL(compressedFile));
    } catch (compressionError) {
      console.error('画像圧縮エラー:', compressionError);
      setError('画像の圧縮に失敗しました。別の画像で再試行してください。');
    }
  };

  const handleCancel = () => {
    if (window.confirm('変更を破棄してプロフィール画面に戻りますか？')) {
      navigate('/profile');
    }
  };

  if (fetchLoading) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="edit-profile-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          登録中...
        </div>
      )}
      <div className="card">
        <h1 className="page-title">プロフィール編集</h1>

        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={handleSubmit} className="edit-profile-form">
          <div className="form-section">
            <h2>基本情報</h2>

            <div className="form-group">
              <label className="form-label">プロフィール画像</label>
              <div style={{display:'flex', alignItems:'flex-end', gap:'12px'}}>
              <div className="profile-image-upload" onClick={() => profileImageInputRef.current?.click()} style={{cursor:'pointer', display:'inline-block'}}>
                {profileImagePreview && !deletePhoto ? (
                  <img
                    src={profileImagePreview}
                    alt="プロフィール"
                    style={{width:'96px', height:'96px', borderRadius:'50%', objectFit:'cover', border:'2px solid var(--primary-green)'}}
                  />
                ) : (
                  <div style={{width:'96px', height:'96px', borderRadius:'50%', background:'#e0e0e0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'32px'}}>
                    👤
                  </div>
                )}
                <p className="form-help" style={{marginTop:'6px'}}>タップして変更（5MB以内・正方形推奨）</p>
              </div>
              {profileImagePreview && !deletePhoto && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  style={{marginBottom:'24px'}}
                  onClick={() => {
                    setDeletePhoto(true);
                    setProfileImageFile(null);
                    setProfileImagePreview('');
                    if (profileImageInputRef.current) profileImageInputRef.current.value = '';
                  }}
                >
                  削除
                </button>
              )}
            </div>
              <input
                ref={profileImageInputRef}
                type="file"
                accept="image/*"
                style={{display:'none'}}
                onChange={handleProfileImageChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">ニックネーム *</label>
              <input
                type="text"
                name="nickname"
                className="form-control"
                value={formData.nickname}
                onChange={handleChange}
                required
                placeholder="山登り太郎"
              />
            </div>

            <div className="form-group">
              <label className="form-label">性別</label>
              <select name="gender" className="form-control" value={formData.gender} onChange={handleChange}>
                {GENDER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">年代</label>
              <select name="ageGroup" className="form-control" value={formData.ageGroup} onChange={handleChange}>
                {AGE_GROUP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">居住都道府県</label>
              <select name="residencePrefecture" className="form-control" value={formData.residencePrefecture} onChange={handleChange}>
                <option value="">未回答</option>
                {PREFECTURES.map(pref => <option key={pref} value={pref}>{pref}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">自己紹介</label>
              <textarea
                name="bio"
                className="form-control"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                placeholder="登山の経験や興味について自由に記載してください。"
              />
            </div>


            <div className="form-group">
              <label className="form-label">月間登山頻度</label>
              <select name="monthlyFrequency" className="form-control" value={formData.monthlyFrequency} onChange={handleChange}>
                {MONTHLY_FREQUENCY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">登山スタイル（複数選択可）</label>
              <div className="experience-checkboxes">
                {HIKING_STYLE_OPTIONS.map(item => (
                  <label key={item.key} className={`experience-checkbox-label${formData.hikingStyle?.includes(item.key) ? ' is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.hikingStyle?.includes(item.key) || false}
                      onChange={() => handleHikingStyleChange(item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">歩行ペース</label>
              <select name="pace" className="form-control" value={formData.pace} onChange={handleChange}>
                {PACE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <p className="form-help">標準コースタイム（CT）を基準に選んでください</p>
            </div>

            <div className="form-group">
              <label className="form-label">体力レベル（8kgの登山ザックを背負ってどのくらい歩けるか）</label>
              <select name="fitnessLevel" className="form-control" value={formData.fitnessLevel} onChange={handleChange}>
                {FITNESS_LEVEL_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">標高帯の経験（複数選択可）</label>
              <div className="experience-checkboxes">
                {ALTITUDE_EXPERIENCE_OPTIONS.map(item => (
                  <label key={item.key} className={`experience-checkbox-label${formData.altitudeExperience?.includes(item.key) ? ' is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.altitudeExperience?.includes(item.key) || false}
                      onChange={() => handleArrayFieldChange('altitudeExperience', item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">技術・経験（複数選択可）</label>
              <div className="experience-checkboxes">
                {TECHNICAL_EXPERIENCE_OPTIONS.map(item => (
                  <label key={item.key} className={`experience-checkbox-label${formData.technicalExperience?.includes(item.key) ? ' is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.technicalExperience?.includes(item.key) || false}
                      onChange={() => handleArrayFieldChange('technicalExperience', item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">所持装備（複数選択可）</label>
              <div className="experience-checkboxes">
                {GEAR_OPTIONS.map(item => (
                  <label key={item.key} className={`experience-checkbox-label${formData.ownedGear?.includes(item.key) ? ' is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.ownedGear?.includes(item.key) || false}
                      onChange={() => handleArrayFieldChange('ownedGear', item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">登山歴</label>
              <textarea
                name="climbingHistory"
                className="form-control"
                value={formData.climbingHistory}
                onChange={handleChange}
                rows={3}
                placeholder="登山を始めた時期や主な経験など"
              />
            </div>

            <div className="form-group">
              <label className="form-label">好きな山</label>
              <textarea
                name="favoriteMountains"
                className="form-control"
                value={formData.favoriteMountains}
                onChange={handleChange}
                rows={3}
                placeholder="特に好きな山や行ってみたい山など"
              />
            </div>

            <div className="form-group">
              <label className="form-label car-checkbox-label">
                <input
                  type="checkbox"
                  name="hasCar"
                  checked={formData.hasCar}
                  onChange={handleChange}
                  style={{width: '18px', height: '18px', marginRight: '10px', accentColor: 'var(--primary-green)'}}
                />
                マイカー所有
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">加入登山保険</label>
              <input
                type="text"
                name="mountainInsurance"
                className="form-control"
                value={formData.mountainInsurance}
                onChange={handleChange}
                placeholder="例: 山岳保険、日山協保険など"
              />
            </div>

            <div className="form-section private-info-section">
              <h2>非公開プロフィール <span className="private-badge">🔒 応募・募集時のみ相手に公開</span></h2>
              <p className="form-help" style={{marginBottom: '16px'}}>以下の情報は、あなたが募集を作成した場合・応募した場合にのみ相手に公開されます。一般公開はされません。スクリーンショット撮影、転載、第三者への共有は禁止です。</p>

              <div className="form-group">
                <label className="form-label">最寄り駅</label>
                <input
                  type="text"
                  name="nearestStation"
                  className="form-control"
                  value={formData.nearestStation}
                  onChange={handleChange}
                  placeholder="例: JR新宿駅"
                />
              </div>

              <div className="form-group">
                <label className="form-label">ヘリコプター救助サービス（ ココヘリ等）番号</label>
                <input
                  type="text"
                  name="cocoheriNumber"
                  className="form-control"
                  value={formData.cocoheriNumber}
                  onChange={handleChange}
                  placeholder="例: C-12345"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>SNSリンク</h2>
            
            <div className="form-group">
              <label className="form-label">YAMAP</label>
              <input
                type="url"
                name="yamapLink"
                className="form-control"
                value={formData.yamapLink}
                onChange={handleChange}
                placeholder="https://yamap.com/users/..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">ヤマレコ</label>
              <input
                type="url"
                name="yamarecoLink"
                className="form-control"
                value={formData.yamarecoLink}
                onChange={handleChange}
                placeholder="https://www.yamareco.com/user/..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">X(Twitter)</label>
              <input
                type="url"
                name="twitterLink"
                className="form-control"
                value={formData.twitterLink}
                onChange={handleChange}
                placeholder="https://x.com/username"
              />
            </div>

            <div className="form-group">
              <label className="form-label">YouTube</label>
              <input
                type="url"
                name="youtubeLink"
                className="form-control"
                value={formData.youtubeLink}
                onChange={handleChange}
                placeholder="https://www.youtube.com/..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Instagram</label>
              <input
                type="url"
                name="instagramLink"
                className="form-control"
                value={formData.instagramLink}
                onChange={handleChange}
                placeholder="https://www.instagram.com/..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfile;
